<template>
  <div class="settings-container step-two">
    <div v-if="maxRecipients" class="subtitle">the selected retailer customer count plus a 2% buffer for new members is ~{{abbrNum(maxRecipients, 0)}}</div>
    <div v-if="isEdit" class="additional-info">your current maximum number of potential recipients is {{ad.credits}} (${{maxCost}})</div>

    <toggle-section-slide
      v-if="hasOneRetailerSelected"
      :shouldOpen="showFilters"
      key="filters"
      toggleText="customer filters"
      wrapperClasses="filters-wrapper"
      @onToggleTextClick="showFilters = !showFilters">
      <sb-async-dropdown slot="mainContent"
        :rows="filtersData.rows"
        :header-rows="filtersData.headerRows"
        :footer-rows="filtersData.footerRows"
        :focused="filtersData.focused"
        :selected.sync="filtersData.selected"
        @selectedRow="filtersData.selectedRow"
        @deselectedRow="filtersData.deselectedRow"/>
      <div v-if="displayFilterPreview" slot="extraContent" class="preview-wrapper">
        <span>filters selected:</span>
        <span v-for="(key, index) in selectedFilters.keys()"
             :key="index"
             class="selected-filter">
          <span class="preview-name">{{ key }}:</span>
          <span class="preview-labels" :class="{'not-last' : index !== selectedFilters.size - 1}">
            {{selectedFilters.get(key).join(', ')}}
          </span>
        </span>
      </div>
    </toggle-section-slide>

    <div class="recipients-wrapper">
      <div class="recipient-inputs">
        <div class="credits-wrapper">
          <label>{{creditsInputConfig.label}}</label>
          <div class="credits-limit">
            <input
                id="credits"
                min="0"
                name="credits"
                step="1"
                type="number"
                v-model="$data[creditsInputConfig.field]"
                @blur="sanitizeInteger">
            <span v-if="isEdit && maxRecipients" class="sub-label">{{textableDifference}} needed to reach all textable members</span>
            <span v-else-if="maxRecipients" class="sub-label">{{abbrNum(maxRecipients, 0)}} total textable members</span>
          </div>
        </div>
        <div class="cost-wrapper">
          <label>{{costInputConfig.label}}</label>
          <div class="cost-container">
            <div class="cost-input">
              <span class="dollar-sign">$</span>
              <input
                  id="cost"
                  min="0"
                  name="cost"
                  type="number"
                  v-model="$data[costInputConfig.field]"
                  @blur="sanitizeCurrency">
            </div>
          </div>
        </div>
      </div>
      <div :class="['single-use-ad', { disabled: isEdit}]">
        <label for="send_once">
          <input
              class="hide"
              :disabled="isEdit"
              id="send_once"
              name="send_once"
              type="checkbox"
              v-model="singleUseAd"
              @change="setSingleUseAd">
          <div :class="['checkmark', { disabled: isEdit}]">
            <div v-if="singleUseAd" class="check"></div>
          </div>
        </label>
        <label>only allow each retailer to send your ad <span>one time</span></label>
      </div>
      <div v-if="isEdit" class="additional-info">you may only increase the recipient count at this time; if you wish to reduce the number of potential recipients, you must end the ad and create a new one</div>
    </div>
    <modal
        v-if="showModal"
        :buttonText="modalConfig.buttonText"
        :showClose="modalConfig.showClose"
        :wrapperClasses="modalConfig.wrapperClasses"
        @close="closeModal"
        @proceed="addToCredits">
      <div slot="header" class="header">{{modalConfig.text}}</div>
      <div slot="body" class="body">
        <p>increase credits by: {{recipientIncrease}}</p>
        <p>cost for recipients increase: ${{costIncrease}}</p>
      </div>
    </modal>
  </div>
</template>

<script>
  import LoadRetailers from '../mixins/LoadRetailers.js'
  import Modal from '../Shared/Modal'
  import ToggleSectionSlide from "../Shared/ToggleSectionSlide.vue"
  import _ from "lodash"
  import { adAPI } from "../../services/api/adAPI"
  import { calculateTotalPagesUtil } from "../../utilities"
  import { NumberFormatters } from '../mixins/NumberFormatters.js'

  export default {
    props: {
      currentAdvertiserId: [Number, String],
      ad: Object,
      isEdit: Boolean,
      selectedRetailers: Array,
      smsRate: Number,
      selectedRetailerIds: Array
    },

    name: 'StepTwo',

    mixins: [LoadRetailers, NumberFormatters],

    components: {
      Modal,
      ToggleSectionSlide
    },

    data() {
      return {
        costIncrease: null,
        costInputConfig: {},
        creditsInputConfig: {},
        maxCost: null,
        modalConfig: {},
        recipientIncrease: null,
        recipientMax: null,
        showModal: false,
        singleUseAd: null,
        filtersData: {
          path: "",
          pathIndex: new Map(),
          rows: [],
          selected: [],
          focused: true,
          headerRows: [],
          footerRows: [],
          selectedRow: state => {
            const selectedSet = this.filtersData.pathIndex.get(this.filtersData.path)
            if (this.filtersData.path === 'categories') selectedSet.add(state.tag)
            else selectedSet.add(state.id)
            this.updateFiltersAndMaxRecipients(selectedSet)
          },
          deselectedRow: state => {
            const selectedSet = this.filtersData.pathIndex.get(this.filtersData.path)
            if (this.filtersData.path === 'categories') selectedSet.delete(state.tag)
            else selectedSet.delete(state.id)
            this.updateFiltersAndMaxRecipients(selectedSet)
          },
        },
        showFilters: false,
        transactionBasedSubGroups: null,
        filterMaxRecipient: null,
        debounceFilterSearch: null,
        selectedFilters: new Map(),
        segmentGroups: ['locations', 'visiting total', 'spending total', 'average spend', 'visit recency'],
        taggingGroup: ['categories'],
        savedFilters: null
      }
    },

    computed: {
      maxRecipients() {
        if (!this.selectedRetailers) { return }
        const buffer = 0.02,
            totalMembers = this.selectedRetailers.map(obj => obj.recipient_sms_size).reduce((a, b) => a + b, 0),
            totalRecipients = this.filtersData.selected?.length > 0 ? this.filterMaxRecipient : totalMembers,
            totalWithBuffer = totalRecipients * (1 + buffer),
            base = totalWithBuffer < 1000 ? 100 : 1000

        return this.roundUpNumber(totalWithBuffer, base)
      },

      textableDifference() {
        const difference = this.maxRecipients - this.ad.credits
        return difference > 0 ? difference : 0
      },

      hasOneRetailerSelected() {
        return this.ad?.retailers?.length === 1
      },

      displayFilterPreview() {
        return this.filtersData.selected?.length > 0
      }
    },

    methods: {
      addToCredits() {
        this.calculateCostIncrease()
        this.recipientMax = parseInt(this.recipientMax) + parseInt(this.recipientIncrease)
        this.recipientIncrease = null
        this.showModal = false
        this.setAdData('credits', this.recipientMax)
      },

      calculateCostIncrease() {
        const cost = (this.recipientIncrease * this.smsRate).toFixed(2)
        this.costIncrease = cost > 0 ? cost : null
      },

      calculateMaxCost() {
        this.maxCost = (this.recipientMax * this.smsRate).toFixed(2)
        this.$emit('setData', 'maxCost', this.maxCost)
      },

      calculateRecipientIncrease() {
        this.recipientIncrease = (this.costIncrease / this.smsRate).toFixed(0)
      },

      capitalizeFirstLetter(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
      },

      closeModal() {
        this.recipientIncrease = null
        this.costIncrease = null
        this.showModal = false
      },

      configCreditsInput() {
        const field = this.isEdit ? 'recipientIncrease' : 'recipientMax',
            label = this.isEdit ? 'additional potential recipients to be added' : 'maximum number of potential recipients'

        this.creditsInputConfig = { field, label }
      },

      configCostInput() {
        const field = this.isEdit ? 'costIncrease' : 'maxCost',
            label = this.isEdit ? 'cost for the above additional recipients' : 'cost for the above number of recipients'

        this.costInputConfig = { field, label }
      },

      sanitizeCurrency() {
        const fieldName = this.costInputConfig.field,
            capitalizedField = this.capitalizeFirstLetter(fieldName)

        this[fieldName] = this[fieldName]?.toString().replace(/[^\d.]/g, '')
        this[`validate${capitalizedField}`]()
      },

      sanitizeInteger() {
        const fieldName = this.creditsInputConfig.field,
            capitalizedField = this.capitalizeFirstLetter(fieldName)

        this[fieldName] = this[fieldName]?.toString().replace(/\D/g, '')
        this[`validate${capitalizedField}`]()
      },

      setAdData(field, value) {
        const dataToUpdate = { [field]: value }
        this.$emit('updateAd', dataToUpdate)
      },

      setErrors(errorKey, message) {
        this.$emit('setPageErrors', errorKey, message)
      },

      setSingleUseAd() {
        this.setAdData('send_once', this.singleUseAd)
      },

      setValues() {
        this.recipientMax = this.ad.credits
        this.singleUseAd = this.ad.send_once
      },

      showIncreaseModal(updateRecipients) {
        if (updateRecipients) { this.calculateRecipientIncrease() }
        this.modalConfig = {
          buttonText: 'confirm',
          text: 'are you sure you want to increase credits',
          wrapperClasses: 'warning-modal',
          action: 'addToCredits',
          showClose: true
        }
        this.showModal = true
      },

      validateCostIncrease() {
        if (this.costIncrease > 0) { this.showIncreaseModal(true) }
      },

      configureRetailers(data) {
        if (data?.merchants) {
          this.$emit('update:selectedRetailers', data.merchants);
        }
      },

      validateMaxCost() {
        this.recipientMax = (this.maxCost/this.smsRate).toFixed(0)
        this.validateRecipientMax()
      },

      validateRecipientIncrease() {
        if (this.recipientIncrease > 0) { this.showIncreaseModal() }
      },

      validateRecipientMax() {
        if (!!this.recipientMax) this.recipientMax = this.recipientMax.toString().replace(/\D/g, '')
        const hasFiltersSelected = this.filtersData.selected?.length > 0
        let isValid, errorMessage

        if (hasFiltersSelected) {
          isValid = this.recipientMax >= this.maxRecipients
          errorMessage = isValid ? '' : 'maximum recipients entered must be greater than or equal to total textable members'
        } else {
          isValid = this.recipientMax > 0
          errorMessage = isValid ? '' : 'recipient max must be greater than 0'
        }

        this.setErrors('recipients', errorMessage)
        this.setAdData('credits', this.recipientMax)
        this.$emit('setData', 'recipientMax', this.recipientMax)
      },

      getRetailerData() {
        let base = `/merchants?brand_id=${this.currentAdvertiserId}`
        this.selectedRetailerIds?.forEach(retailer => { base += `&q[id_in][]=${retailer}` })
        this.loadRetailers(`${base}`)
      },


// ----------- CUSTOMER FILTERS ON AD --------------------//

      createParentGroup({label, path, id, subGroups, classes}) {
        let badgePath = path
        if (path === "transaction based") {
          badgePath = this.checkTransactionBasedSelectedItem()
        }
        return {
          slot: "parent-row",
          state: {
            label: label,
            path: path,
            id: id,
            subGroups: subGroups,
            badge: this.filtersData.pathIndex.get(badgePath)?.size,
            onClick: state => {
              this.filtersData.rows = ["loading-row"]
              return this.setSubGroupPathRows({group: state})
            }
          },
          ...!!classes && { classes: classes }
        }
      },

      addParentGroupsData(groupsData) {
        const parentsData = groupsData.groups.map(group => (
          this.createParentGroup({label: group.name, path: group.name, id: group.id})
        ))
        parentsData.push(
          this.createParentGroup({
            label: "transaction based",
            path: "transaction based",
            id: "transaction_groups",
            subGroups: groupsData.transaction_groups
          })
        )
        this.transactionBasedSubGroups = groupsData.transaction_groups
        this.filtersData.rows = parentsData
      },

      async setSubGroupPathRows({group, currentPage = 0, query = null}) {
        const path = group.path

        this.filtersData.path = path

        if (!this.filtersData.pathIndex.has(path)) {
          this.filtersData.pathIndex.set(path, new Set())
        }

        let subRowsData, processedData

        const backRow = {
          slot: "go-back-row",
          state: {
            label: path,
            onClick: () => this.getGroups()
          }
        }

        let queryRow = {
          slot: "query-row",
          state: {
            value: query || "",
            update: (newQuery) => {
              this.filtersData.rows = ["loading-row"]
              this.filtersData.footerRows = []
              queryRow.state.value = newQuery
              this.debounceFilterSearch({group: group, query: newQuery})
            }
          }
        }

        let paginationRow = null

        switch (path) {
          case "transaction based":
            queryRow = {
              slot: "query-row",
              classes: "transaction-based",
              state: {
                value: "a maximum of one transaction based category can be selected",
                update: (newValue) => {}
              }
            }
            const selectedSubGroup = this.checkTransactionBasedSelectedItem()

            subRowsData = group.subGroups.map(subGroup => (
              this.createParentGroup({
                label: subGroup.name,
                path: subGroup.name,
                id: subGroup.id,
                classes: !selectedSubGroup || (subGroup.name === selectedSubGroup) ? 'enabled' : 'disabled'
              })
            ))
            break;
          case "categories":
            try {
              processedData = await this.getTaggings(group.label, currentPage + 1, query)
              const processedRows = this.generateTaggingRows({group, processedData, page: currentPage, query})
              if (!processedRows?.queryRow) {
                subRowsData = processedRows.rowsData
                paginationRow = processedRows.paginationRow
              } else {
                queryRow = processedRows.queryRow
              }
            } catch(error) {
              this.setErrors('groups', error)
            }
            break;
          case "locations":
            try {
              processedData = await this.getSegments(group.id, currentPage + 1, query)
              const processedRows = this.generateSegmentRows({
                group, processedData,
                page: currentPage,
                query
              })
              if (!processedRows?.queryRow) {
                subRowsData = processedRows.rowsData
                paginationRow = processedRows.paginationRow
              } else {
                queryRow = processedRows.queryRow
              }
            } catch(error) {
              this.setErrors('groups', error)
            }
            break;
          case "spending total":
          case "visiting total":
          case "average spend":
          case "visit recency":
            try {
              processedData = await this.getSegments(group.id, currentPage + 1, query)
              const processedRows = this.generateSegmentRows({
                group, processedData,
                page: currentPage,
                changeBackRowAction: true,
                query
              })
              if (!processedRows?.queryRow) {
                subRowsData = processedRows.rowsData
                paginationRow = processedRows.paginationRow
                backRow.state.onClick = processedRows.backRowAction
              } else {
                queryRow = processedRows.queryRow
              }
            } catch(error) {
              this.setErrors('groups', error)
            }
            break;
        }

        this.filtersData.headerRows = [backRow, queryRow]
        this.filtersData.rows = subRowsData
        if (!!paginationRow) this.filtersData.footerRows = [paginationRow]
      },

      checkTransactionBasedSelectedItem() {
        let selectedSubGroup = ""
        const transactionGroups = ['spending total', 'visiting total', 'average spend', 'visit recency']
        transactionGroups.forEach(group => {
          if (this.filtersData.pathIndex.get(group)?.size > 0) {
            selectedSubGroup = group
          }
        })
        return selectedSubGroup
      },

      async getSegments(groupId, page, searchTerm) {
        const data = await adAPI.getSegments(this.currentAdvertiserId, this.ad.ads_merchants[0], groupId, page, searchTerm)
        if (data.segments?.length > 0) {
          data.segments = await Promise.all(data.segments.map(async (segment) => {
            const countData = await adAPI.getSegmentCount({
              advertiserId: this.currentAdvertiserId,
              adMerchantId: this.ad.ads_merchants[0],
              itemId: segment.id
            })
            segment.sms = countData?.sms
            return segment
          }))
        }
        return data
      },

      generateSegmentRows({group, processedData, page, changeBackRowAction, query}) {
        let rowsData, paginationRow, queryRow, backRowAction
        if (processedData && processedData.segments?.length > 0) {
          rowsData = processedData.segments.map(segment => ({
            slot: 'toggle-row',
            state: {
              id: segment.id,
              label: segment.name,
              selected: this.savedFilters.merchant_segment_ids.includes(segment.id),
              textCount: segment.sms,
              root: group.path
            }
          }))

          paginationRow = {
            slot: "pagination-row",
            state: {
              page: page,
              pages: calculateTotalPagesUtil(processedData.pagination.total_elements, processedData.pagination.per_page),
              update: (newPage) => this.setSubGroupPathRows({group: group, currentPage: newPage, query})
            }
          }
        } else {
          if (!!query) {
            rowsData = [{
              slot: "text-row",
              state: { message: "no search results" }
            }]
          } else {
            queryRow = {
              slot: "query-row",
              classes: "unconfigured",
              state: { value: "group not configured", update: (newValue) => {} }
            }
          }
        }

        if (changeBackRowAction) {
          backRowAction = () => {
            this.setSubGroupPathRows({group: {
              path: "transaction based",
              subGroups: this.transactionBasedSubGroups
            }})
          }
        }

        return {rowsData, paginationRow, backRowAction, queryRow}
      },

      async getTaggings(groupContext, page, searchTerm) {
        const data = await adAPI.getTags(
          this.currentAdvertiserId,
          {
            ads_merchant_id: this.ad.ads_merchants[0],
            context: groupContext,
            page: page,
            search: searchTerm
          }
        )

        if (data.taggings?.length > 0) {
          data.taggings = await Promise.all(data.taggings.map(async (tagging) => {
            const tagCount = await adAPI.getTagCount({
              advertiserId: this.currentAdvertiserId,
              ads_merchant_id: this.ad.ads_merchants[0],
              context: groupContext,
              itemId: tagging.tag
            })
            tagging.sms = tagCount?.sms
            return tagging
          }))
        }

        return data
      },

      generateTaggingRows({group, processedData, page, query}) {
        let rowsData, paginationRow, queryRow
        if (processedData && processedData.taggings?.length > 0) {
          rowsData = processedData.taggings.map((tagging) => {
            const tag = {
              slot: "toggle-row",
              state: {
                id: tagging.tag_id,
                label: tagging.name,
                tag: tagging.tag,
                textCount: tagging.sms,
                selected: this.savedFilters.merchant_tag_ids.includes(tagging.tag_id),
                root: group.path
              }
            }
            return tag
          })

          paginationRow = {
            slot: "pagination-row",
            state: {
              page: page,
              pages: calculateTotalPagesUtil(processedData.pagination.total_elements, processedData.pagination.per_page),
              update: newPage => {
                this.setSubGroupPathRows({group: group, currentPage: newPage, query})
              }
            }
          }
        } else {
          if (!!query) {
            rowsData = [{
              slot: "text-row",
              state: { message: "no search results" }
            }]
          } else {
            queryRow = {
              slot: "query-row",
              classes: "unconfigured",
              state: { value: "group not configured", update: (newValue) => {} }
            }
          }
        }
        return {rowsData, paginationRow, queryRow}
      },

      async updateFiltersAndMaxRecipients(selectedSet) {
        const isSegmentGroup = this.segmentGroups.includes(this.filtersData.path)
        const updateKey = isSegmentGroup ? 'selected_merchant_segment_ids' : 'category_list'
        let updateValue

        if (isSegmentGroup) {
          const values = this.filtersData.selected.filter(selected => this.segmentGroups.includes(selected.root))
          updateValue = values.flatMap(value => value.id)
        } else {
          updateValue = (Array.from(selectedSet)).join(',')
        }

        const params = {
          ads_merchants_attributes: [{
            id: this.ad.ads_merchants[0],
            [updateKey]: updateValue
          }],
          id: this.ad.id
        }
        try {
          await adAPI.updateAd(this.currentAdvertiserId, params)
          const updatedMaxRecipients = await adAPI.getMaxRecipients(this.currentAdvertiserId, this.ad.ads_merchants[0])
          this.filterMaxRecipient = updatedMaxRecipients.total_textable_members
          this.loadSavedFilters(false)
        } catch(error) {
          this.setErrors('groups', error)
        }
      },

      async loadSavedFilters(loadParentGroups = true) {
        try {
          this.savedFilters = await adAPI.getFilters(this.currentAdvertiserId, this.ad.ads_merchants[0])
          const groupsWithFilters = this.savedFilters.selected_groups.filter(({display_names}) => !!display_names)
          this.showFilters = groupsWithFilters?.length > 0

          if (this.showFilters) {
            const savedTags = this.savedFilters.selected_groups.filter(({name}) => name === 'categories')

            this.filtersData.selected = []

            this.savedFilters.merchant_tag_ids.forEach(id => {
              this.filtersData.selected.push({ id: id, root: 'categories' })
            })

            groupsWithFilters.forEach(selected => {
              const isSegmentGroup = this.segmentGroups.includes(selected.name)
              if (isSegmentGroup) {
                selected.merchant_segment_ids.forEach(id => {
                  this.filtersData.selected.push({ id: id, root: selected.name })
                })
              }
              const value = isSegmentGroup ? selected.merchant_segment_ids : (savedTags[0]?.tags || [])
              this.filtersData.pathIndex.set(selected.name, new Set(value))
            })

            this.loadFiltersPreview(groupsWithFilters)
          }
          if (loadParentGroups) this.getGroups()
        } catch(error) {
          this.setErrors('groups', error)
        }
      },

      loadFiltersPreview(groupsWithFilters) {
        this.selectedFilters = new Map()
        groupsWithFilters.forEach(group => {
          this.selectedFilters.set(group.name, group.display_names)
        })
      },

      async getGroups() {
        if (!this.hasOneRetailerSelected) {
          this.filterMaxRecipient = null
          return
        }

        this.filtersData.path = ""
        this.filtersData.headerRows = []
        this.filtersData.footerRows = []
        this.filtersData.rows = ["loading-row"]
        this.debounceFilterSearch = _.debounce(this.setSubGroupPathRows, 500)

        try {
          const groupsData = await adAPI.getGroups(this.currentAdvertiserId, this.ad.ads_merchants[0])
          this.addParentGroupsData(groupsData)
        } catch (error) {
          this.setErrors('groups', error)
        }
      }
    },

    created() {
      if (this.ad) { this.setValues() }
      this.calculateMaxCost()
      if (this.isEdit) { this.calculateCostIncrease() }
      this.configCreditsInput()
      this.configCostInput()
      this.getRetailerData()
      if (this.hasOneRetailerSelected) {
        this.loadSavedFilters()
      }
    },

    watch: {
      ad: function () {
        this.setValues()
      },

      recipientIncrease: function () {
        this.calculateCostIncrease()
      },

      recipientMax: function () {
        if (this.recipientMax) { this.calculateMaxCost() }
      },
    },
  }
</script>

<style lang="sass">
@import '../../assets/sass/colors'

.settings-container.step-two
  .subtitle
    margin-top: -1.5rem

  .additional-info
    color: $title-color
    font-size: 1.25rem
    line-height: 1.2
    margin: .5rem auto
    max-width: 400px

  .recipients-wrapper
    align-items: flex-start
    display: inline-flex
    flex-direction: column
    margin: 2rem auto

  .recipient-inputs
    display: flex
    flex-direction: column
    align-items: flex-end

  .credits-wrapper, .cost-wrapper, .single-use-ad
    align-items: flex-start
    display: flex
    justify-content: flex-start
    margin: 0 0 2rem 0

    label
      margin: .7rem .5rem 0 0

    input
      text-align: center

  .credits-wrapper, .cost-wrapper
    @media only screen and (max-width: 40em)
      align-items: center
      flex-direction: column
      justify-content: center

    label
      @media only screen and (max-width: 40em)
        margin: .7rem .5rem .5rem 0

  .credits-limit
    display: flex
    flex-direction: column
    width: 200px

  .sub-label
    color: $title-color
    font-size: .9rem
    line-height: 1.2
    margin: -1rem auto 0 auto
    max-width: 180px

  .cost-wrapper
    font-size: 1.25rem

    input
      border: none
      margin-bottom: 0
      padding: .5rem 1rem .5rem 0
      width: auto

    .cost-input
      border-radius: 32px
      border: 1px solid $primary-color
      display: flex
      align-items: center
      font-size: 1.25rem
      width: 200px

      input
        width: 100%

      .dollar-sign
        padding: .5rem 0 .5rem 1rem

  .single-use-ad
    align-items: center

    &.disabled
      opacity: .5

    label
      margin: 0

      span
        color: $primary-color
        font-weight: bold

    .checkmark
      background-color: #fff
      border: 1px solid $primary-color
      cursor: pointer
      display: flex
      height: 28px
      margin: 0 .5rem 0 0
      width: 28px

      &.disabled
        cursor: not-allowed

  .single-use-ad input:checked ~ .checkmark
    background-color: $primary-color

  .filters-wrapper
    margin-top: 2rem
    .slide-section__slider
      &.slide-down
        max-height: 600px
        overflow: initial
      &.slide-up
        max-height: 0
    .sb-base-dropdown
      display: block
      margin-bottom: 15px
      .label-container
        visibility: hidden
        height: 0
      .sb-content
        background-color: unset
        border: none
        position: unset
        .sb-header, .sb-footer
          margin: 0 auto
          max-width: 470px
          .query-container
            border: none
        .sb-content-items
          margin: 0 auto
          max-height: 175px
          .dropdown-item.text-row
            color: $negative-color
            cursor: unset
            pointer-events: none
          .dropdown-item.disabled
            color: $negative-color
            pointer-events: none
          .dropdown-item
            .sb-item-label
              text-align: left
            .counters
              font-size: .7em
              padding-left: 5px
              text-align: left
            .selected-badge
              padding: .2rem .4rem
    .transaction-based, .unconfigured
      cursor: unset
      .query-container
        border: none
        .clear-search
          display: none
        .sb-query
          border: none
          margin-bottom: 0
          text-align: center
          color: $negative-color
          pointer-events: none
    .preview-wrapper
      width: 450px
      margin: 0 auto
      text-align: left
      padding: 0 5px 0 15px
      .selected-filter
        margin-left: 8px
        font-size: smaller
        .preview-name
          margin-right: 5px
          font-weight: bold
        .preview-labels.not-last
          border-right: 1px solid $primary-color
          padding-right: 10px
</style>
